import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [data, setData] = useState(null);
  const sessionId = searchParams.get("session_id");

  const pollStatus = useCallback(async (attempts = 0) => {
    if (!sessionId || attempts >= 5) {
      setStatus(attempts >= 5 ? "timeout" : "error");
      return;
    }
    try {
      const res = await axios.get(`${API}/checkout/stripe/status/${sessionId}`);
      setData(res.data);
      if (res.data.payment_status === "paid") {
        setStatus("success");
      } else if (res.data.status === "expired") {
        setStatus("expired");
      } else {
        setTimeout(() => pollStatus(attempts + 1), 2000);
      }
    } catch {
      setStatus("error");
    }
  }, [sessionId]);

  useEffect(() => { pollStatus(); }, [pollStatus]);

  return (
    <div className="min-h-screen pt-24 flex items-center justify-center" data-testid="checkout-success-page">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md mx-auto text-center p-10 rounded-2xl glass-light"
      >
        {status === "checking" && (
          <>
            <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
            <h2 className="text-2xl font-serif font-bold text-white mb-2">Processing Payment...</h2>
            <p className="text-muted-foreground">Please wait while we confirm your purchase.</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-400" />
            </div>
            <h2 className="text-2xl font-serif font-bold text-white mb-2" data-testid="purchase-success">Purchase Complete!</h2>
            <p className="text-muted-foreground mb-6">Thank you for your purchase! We'll be in touch with next steps.</p>
            <a href="/" className="btn-primary inline-block">Back to Home</a>
          </>
        )}
        {(status === "error" || status === "timeout" || status === "expired") && (
          <>
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-6" />
            <h2 className="text-2xl font-serif font-bold text-white mb-2">Payment Status Unknown</h2>
            <p className="text-muted-foreground mb-6">We couldn't confirm your payment. If you were charged, please contact us.</p>
            <a href="/contact" className="btn-primary inline-block">Contact Us</a>
          </>
        )}
      </motion.div>
    </div>
  );
}
